// MatchResponse Component
// Handle incoming match requests (Fold, Call, Raise)

import React, { useState } from 'react';
import { MatchRequest, BetLevel } from '../../../types/swipe';
import { BET_LEVEL_DETAILS } from '../../../constants/swipe';
import {
  ResponseContainer,
  ResponseCard,
  TheirBetSection,
  TheirBetLabel,
  TheirBetDisplay,
  TheirMessage,
  ResponseActions,
  ResponseActionButton,
  ActionIcon,
  ActionDescription,
  TimeoutWarning
} from '../../../styles/Swipe/BettingInterface.styles';

interface MatchResponseProps {
  matchRequest: MatchRequest;
  onResponse: (action: 'accept' | 'decline' | 'counter', counterBet?: BetLevel) => Promise<void>;
}

export const MatchResponse: React.FC<MatchResponseProps> = ({
  matchRequest,
  onResponse
}) => {
  const [isResponding, setIsResponding] = useState(false);
  const [showCounterOptions, setShowCounterOptions] = useState(false);

  // Calculate time remaining
  const getTimeRemaining = () => {
    const now = new Date().getTime();
    const sent = new Date(matchRequest.sentAt).getTime();
    const elapsed = (now - sent) / (1000 * 60 * 60); // hours
    const remaining = 48 - elapsed; // 48 hour window

    if (remaining <= 0) return { hours: 0, isExpired: true, isUrgent: false };
    return {
      hours: Math.floor(remaining),
      isExpired: false,
      isUrgent: remaining < 12
    };
  };

  const { hours: remainingHours, isExpired, isUrgent } = getTimeRemaining();
  const betDetails = BET_LEVEL_DETAILS[matchRequest.betLevel];

  // Handle response
  const handleResponse = async (action: 'accept' | 'decline' | 'counter', counterBet?: BetLevel) => {
    try {
      setIsResponding(true);
      await onResponse(action, counterBet);
    } catch (error) {
      console.error('Error responding to match:', error);
      alert('Failed to send response. Please try again.');
    } finally {
      setIsResponding(false);
    }
  };

  // Get counter bet options (higher stakes only)
  const getCounterOptions = (): BetLevel[] => {
    const levels: BetLevel[] = ['low', 'medium', 'high'];
    const currentIndex = levels.indexOf(matchRequest.betLevel);
    return levels.slice(currentIndex + 1); // Only higher stakes
  };

  const counterOptions = getCounterOptions();

  if (isExpired) {
    return (
      <ResponseCard>
        <TimeoutWarning $isUrgent={true}>
          ⚠️ This match request has expired
        </TimeoutWarning>
      </ResponseCard>
    );
  }

  return (
    <ResponseCard>
      {/* Timeout Warning */}
      {isUrgent && (
        <TimeoutWarning $isUrgent={isUrgent}>
          ⏰ {remainingHours} hours left to respond!
        </TimeoutWarning>
      )}

      {/* Their Bet */}
      <TheirBetSection>
        <TheirBetLabel>Their Move</TheirBetLabel>
        <TheirBetDisplay $betLevel={matchRequest.betLevel}>
          <ActionIcon>{betDetails.icon}</ActionIcon>
          <div>
            <strong>{betDetails.label}</strong>
            <br />
            <small>{betDetails.description}</small>
          </div>
        </TheirBetDisplay>

        {/* Their Message */}
        <TheirMessage>
          "{matchRequest.initialMessage || 'Hey! Would love to connect.'}"
        </TheirMessage>
      </TheirBetSection>

      {/* Response Actions */}
      {!showCounterOptions ? (
        <ResponseActions>
          {/* Fold (Decline) */}
          <ResponseActionButton
            $variant="decline"
            onClick={() => handleResponse('decline')}
            disabled={isResponding}
          >
            <ActionIcon>🃏</ActionIcon>
            <div>
              <strong>Fold</strong>
              <ActionDescription>Not interested</ActionDescription>
            </div>
          </ResponseActionButton>

          {/* Call (Accept) */}
          <ResponseActionButton
            $variant="accept"
            onClick={() => handleResponse('accept')}
            disabled={isResponding}
          >
            <ActionIcon>✅</ActionIcon>
            <div>
              <strong>Call</strong>
              <ActionDescription>Accept their bet</ActionDescription>
            </div>
          </ResponseActionButton>

          {/* Raise (Counter Offer) */}
          {counterOptions.length > 0 && (
            <ResponseActionButton
              $variant="counter"
              onClick={() => setShowCounterOptions(true)}
              disabled={isResponding}
            >
              <ActionIcon>📈</ActionIcon>
              <div>
                <strong>Raise</strong>
                <ActionDescription>Counter with higher stakes</ActionDescription>
              </div>
            </ResponseActionButton>
          )}
        </ResponseActions>
      ) : (
        // Show counter offer options
        <ResponseActions>
          <TheirBetLabel style={{ marginBottom: '12px', width: '100%' }}>
            Select Your Counter Offer
          </TheirBetLabel>
          {counterOptions.map(level => {
            const details = BET_LEVEL_DETAILS[level];
            return (
              <ResponseActionButton
                key={level}
                $variant="counter"
                onClick={() => handleResponse('counter', level)}
                disabled={isResponding}
              >
                <ActionIcon>{details.icon}</ActionIcon>
                <div>
                  <strong>{details.label}</strong>
                  <ActionDescription>{details.description}</ActionDescription>
                </div>
              </ResponseActionButton>
            );
          })}
          <ResponseActionButton
            $variant="decline"
            onClick={() => setShowCounterOptions(false)}
            disabled={isResponding}
          >
            <ActionIcon>←</ActionIcon>
            <div>
              <strong>Back</strong>
            </div>
          </ResponseActionButton>
        </ResponseActions>
      )}
    </ResponseCard>
  );
};

